/**
 * URL Helper Utility for Cross-Subdomain Navigation
 * Handles navigation between main site, staff portal, and student portal
 */

class URLHelper {
  constructor() {
    this.mainUrl = process.env.VUE_APP_MAIN_URL || "http://localhost:8080";
    this.staffUrl =
      process.env.VUE_APP_STAFF_URL || "http://localhost:8080/staff";
    this.portalUrl =
      process.env.VUE_APP_PORTAL_URL || "http://localhost:8080/app";
    this.isDevelopment = process.env.NODE_ENV === "development";

    // Fix production URLs if not properly set
    if (!this.isDevelopment) {
      if (this.staffUrl.includes("localhost")) {
        this.staffUrl = "https://staff.firstcitypoly.edu.ng";
      }
      if (this.portalUrl.includes("localhost")) {
        this.portalUrl = "https://portal.firstcitypoly.edu.ng";
      }
      if (this.mainUrl.includes("localhost")) {
        this.mainUrl = "https://firstcitypoly.edu.ng";
      }
    }
  }

  /**
   * Map subdomain paths to development paths
   * @param {string} path - Subdomain path
   * @param {string} module - 'staff' or 'portal'
   * @returns {string} Development path
   */
  mapToDevPath(path, module) {
    if (module === "portal") {
      // Portal path mappings for development
      const portalMappings = {
        "/": "/app", // Portal home -> /app
        "/dashboard": "/app", // Dashboard -> /app (empty path in router)
        "/finance": "/app/finance",
        "/academics": "/app/academics",
        "/resources": "/app/resources",
        "/settings": "/app/settings",
        "/application": "/app/appForm",
      };
      return portalMappings[path] || `/app${path}`;
    }
    if (module === "staff") {
      // Staff path mappings for development
      const staffMappings = {
        "/": "/staff", // Staff home -> /staff
        "/dashboard": "/staff", // Dashboard -> /staff (empty path in router)
        "/settings": "/staff/settings",
        "/students": "/staff/students",
        "/staff": "/staff/staff",
        "/payments": "/staff/payments",
        "/academics": "/staff/academics",
        "/scholarship": "/staff/scholarship",
        "/notifications": "/staff/notifications",
        "/careers": "/staff/careers",
        "/exams": "/staff/exams",
        "/database": "/staff/database",
      };
      return staffMappings[path] || `/staff${path}`;
    }

    return path;
  }

  /**
   * Navigate to main website
   * @param {string} path - Path on main site (e.g., "/apply", "/about")
   */
  goToMain(path = "/") {
    const url = `${this.mainUrl}${path}`;
    window.location.href = url;
  }

  /**
   * Navigate to staff portal
   * @param {string} path - Path in staff portal (e.g., "/dashboard", "/settings")
   * @param {object} [router] - Vue Router instance. If provided, uses router.push in dev mode.
   */
  goToStaff(path = "/", router = null) {
    if (this.isDevelopment) {
      const devPath = this.mapToDevPath(path, "staff");
      if (router) {
        router.push(devPath);
      } else {
        window.location.href = devPath;
      }
    } else {
      // In production, redirect to subdomain
      window.location.href = `${this.staffUrl}${path === "/" ? "" : path}`;
    }
  }

  /**
   * Navigate to student portal
   * @param {string} path - Path in student portal (e.g., "/dashboard", "/finance")
   * @param {object} [router] - Vue Router instance. If provided, uses router.push in dev mode.
   */
  goToPortal(path = "/", router = null) {
    if (this.isDevelopment) {
      const devPath = this.mapToDevPath(path, "portal");
      if (router) {
        router.push(devPath);
      } else {
        window.location.href = devPath;
      }
    } else {
      // In production, redirect to subdomain
      window.location.href = `${this.portalUrl}${path === "/" ? "" : path}`;
    }
  }

  /**
   * Get full URL for a portal path (for href attributes)
   * @param {string} path - Path in student portal
   * @returns {string} Full URL
   */
  getPortalUrl(path = "/") {
    if (this.isDevelopment) {
      return this.mapToDevPath(path, "portal");
    }
    return `${this.portalUrl}${path === "/" ? "" : path}`;
  }

  /**
   * Translate main app paths to portal subdomain paths
   * @param {string} appPath - Path from main app (e.g., "/app/appForm")
   * @returns {string} Corresponding portal path
   */
  translateToPortalPath(appPath) {
    // Remove /app prefix if present
    const cleanPath = appPath.startsWith("/app")
      ? appPath.replace("/app", "")
      : appPath;

    // Map main app paths to portal subdomain paths
    const pathMappings = {
      "/appForm": "/application",
      "/application": "/application", // Direct mapping
      "/finance": "/finance",
      "/academics": "/academics",
      "/resources": "/resources",
      "/settings": "/settings",
      "/dashboard": "/dashboard",
      "": "/dashboard", // Empty path goes to dashboard
      "/": "/dashboard",
    };

    return pathMappings[cleanPath] || cleanPath;
  }

  /**
   * Get correct URL for any path, handling cross-module navigation
   * @param {string} path - Any path from notifications, links, etc.
   * @returns {string} Correct URL for current environment
   */
  getCorrectUrl(path) {
    if (!path)
      return this.getCurrentModule() === "portal"
        ? "/"
        : this.getPortalUrl("/");

    // Handle different path types
    if (path.startsWith("/app/")) {
      // Main app portal paths - translate to proper subdomain paths
      const portalPath = this.translateToPortalPath(path);

      if (this.isDevelopment) {
        // In development, return the original path
        return path;
      } else {
        // In production, use portal subdomain with translated path
        return `${this.portalUrl}${portalPath}`;
      }
    } else if (path.startsWith("/staff/")) {
      // Staff paths
      const staffPath = path.replace("/staff", "");
      if (this.isDevelopment) {
        return this.mapToDevPath(staffPath || "/", "staff");
      } else {
        // Map /staff/dashboard to root path in production
        if (staffPath === "/dashboard" || staffPath === "") {
          return this.staffUrl;
        }
        return `${this.staffUrl}${staffPath}`;
      }
    } else if (path === "/app" || path === "/app/") {
      // Portal root path /app only
      if (this.isDevelopment) {
        return "/app";
      } else {
        return `${this.portalUrl}/dashboard`;
      }
    } else if (path.startsWith("/")) {
      // Absolute paths - always treat as main site paths
      return `${this.mainUrl}${path}`;
    } else {
      // Relative paths - assume portal context
      return this.getPortalUrl(`/${path}`);
    }
  }

  /**
   * Get full URL for a staff path (for href attributes)
   * @param {string} path - Path in staff portal
   * @returns {string} Full URL
   */
  getStaffUrl(path = "/") {
    if (this.isDevelopment) {
      return this.mapToDevPath(path, "staff");
    }
    return `${this.staffUrl}${path === "/" ? "" : path}`;
  }

  /**
   * Get full URL for main site path
   * @param {string} path - Path on main site
   * @returns {string} Full URL
   */
  getMainUrl(path = "/") {
    return `${this.mainUrl}${path}`;
  }

  /**
   * Get the correct URL for the registration/apply page
   * @returns {string} Full URL for registration
   */
  getApplyUrl() {
    return `${this.mainUrl}/apply`;
  }

  /**
   * Get the correct path for the registration/apply page (for RouterLink)
   * @returns {string} Path for registration
   */
  getApplyPath() {
    return "/apply";
  }

  /**
   * Determine current module based on current URL
   * @returns {string} "main", "staff", or "portal"
   */
  getCurrentModule() {
    const hostname = window.location.hostname;
    if (hostname.includes("staff.")) return "staff";
    if (hostname.includes("portal.")) return "portal";
    return "main";
  }

  /**
   * Navigate to any path, handling cross-module navigation automatically
   * @param {string} path - Any path (main, portal, staff)
   */
  navigateTo(path) {
    const url = this.getCorrectUrl(path);
    window.location.href = url;
  }
}

// Create singleton instance
const urlHelper = new URLHelper();

export default urlHelper;
